// Admin audit logging helper
// Inserts a row into admin_action_log after every admin mutation.
// Returns { error }: the admin action has already succeeded by the time this runs, so a failed
// audit write never blocks or undoes it — but the caller must show the failure to the admin
// (auditFailureMessage), never swallow it (brief 2026-09-28-addendum-5-follow-ups-and-chat-report-audit,
// item 4). supabaseFetch returns a refusal as `error` rather than throwing, so it is read here.

import { supabaseFetch } from '../lib/supabase';

const getAdminId = () => {
  const storageKey = Object.keys(localStorage).find(
    k => k.startsWith('sb-') && k.endsWith('-auth-token')
  );
  const session = storageKey ? JSON.parse(localStorage.getItem(storageKey)) : null;
  return session?.user?.id || null;
};

// PostgREST sends its refusal as a JSON body; the database's own message is what is worth showing.
const reasonOf = (error) => {
  const raw = error?.message || '';
  try {
    const parsed = JSON.parse(raw);
    return parsed?.message || raw;
  } catch {
    return raw;
  }
};

/**
 * Log an admin action.
 *
 * @param {object} params
 * @param {string} params.actionType   - Must match CHECK constraint in admin_action_log
 * @param {string} params.targetType   - 'article' | 'comment' | 'user' | 'content_report' | 'error_report'
 * @param {string} params.targetId     - UUID of the affected row
 * @param {string} [params.reason]     - Optional free-text reason (shown to affected user)
 * @param {object} [params.metadata]   - Optional JSONB bag for extra context
 * @returns {Promise<{error: null | {noSession?: boolean, reason?: string}}>}
 */
export const logAdminAction = async ({ actionType, targetType, targetId, reason, metadata }) => {
  const adminId = getAdminId();
  if (!adminId) return { error: { noSession: true } };

  try {
    const { error } = await supabaseFetch.insert('admin_action_log', {
      admin_id: adminId,
      action_type: actionType,
      target_type: targetType,
      target_id: targetId,
      reason: reason || null,
      metadata: metadata || {}
    }, { returnMinimal: true });
    if (error) {
      console.error('[AuditLog] Failed to log admin action:', error);
      return { error: { reason: reasonOf(error) } };
    }
    return { error: null };
  } catch (err) {
    console.error('[AuditLog] Failed to log admin action:', err);
    return { error: { reason: err?.message || '' } };
  }
};

/**
 * The message to show the admin when one or more audit writes failed, or null when none did.
 * The action itself succeeded; the message says so.
 *
 * @param {function} t        - the caller's t() (admin namespace)
 * @param {Array<{error}>|{error}} results - what logAdminAction returned (one, or several)
 * @returns {string|null}
 */
export const auditFailureMessage = (t, results) => {
  const list = Array.isArray(results) ? results : [results];
  const failed = list.filter(r => r?.error);
  if (failed.length === 0) return null;
  if (failed.some(r => r.error.noSession)) return t('auditLogNoSession');
  const reason = failed[0].error.reason || '';
  if (list.length > 1) {
    return t('auditLogFailedSome', { count: failed.length, total: list.length, reason });
  }
  return t('auditLogFailed', { reason });
};
